import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  Email,
  Phone,
  AccountCircle,
  ArrowForward,
  CheckCircle,
  Error,
  VerifiedUser,
  Lock,
} from '@mui/icons-material';

import { motion, AnimatePresence } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser, selectAuth, clearError } from '../../store/slices/authSlice';
import logoImage from '../assests/cariya-logo.jpeg';

const Login = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  
  // Get auth state from Redux
  const { isLoading, error, isAuthenticated, user } = useAppSelector(selectAuth);

  // Local state
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
  });

  const [activeField, setActiveField] = useState('');
  const [formValid, setFormValid] = useState({
    email: false,
    phoneNumber: false,
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = user.role === 'admin' ? '/dashboard' : `/partner/${user.partnerId}`;
      navigate(redirectPath);
    }
  }, [isAuthenticated, user, navigate]);

  // Clear error when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    // Phone number should start with + and have at least 10 digits
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Real-time validation
    if (name === 'email') {
      setFormValid(prev => ({ ...prev, email: validateEmail(value) }));
    } else if (name === 'phoneNumber') {
      setFormValid(prev => ({ ...prev, phoneNumber: validatePhoneNumber(value) }));
    }
  };

  const handleFocus = (fieldName) => {
    setActiveField(fieldName);
  };

  const handleBlur = () => {
    setActiveField('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formValid.email || !formValid.phoneNumber) {
      return;
    }

    dispatch(loginUser(formData));
  };

  const handleDemoLogin = (role) => {
    const demoCredentials = {
      admin: { email: 'admin@cariya.com', phoneNumber: '+1234567890' },
      partner: { email: 'partner@cariya.com', phoneNumber: '+254700000000' }
    };
    
    setFormData(demoCredentials[role]);
    setFormValid({
      email: true,
      phoneNumber: true,
    });
    
    // Auto-submit after a short delay
    setTimeout(() => {
      dispatch(loginUser(demoCredentials[role]));
    }, 500);
  };

  const isFormValid = formValid.email && formValid.phoneNumber;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, #f92955 0%, #d41e3f 50%, #b01a35 100%),
          radial-gradient(circle at 20% 80%, rgba(187, 255, 193, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(187, 255, 193, 0.1) 0%, transparent 50%)
        `,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Enhanced Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 80%, rgba(187, 255, 193, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(187, 255, 193, 0.06) 0%, transparent 50%)
          `,
          zIndex: 0,
        }}
      />
      
      {/* Floating Elements with Enhanced Animation */}
      <motion.div
        animate={{
          y: [0, -30, 0],
          rotate: [0, 8, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '15%',
          left: '8%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(187, 255, 193, 0.2), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 1,
        }}
      />
      
      <motion.div
        animate={{
          y: [0, 25, 0],
          rotate: [0, -8, 0],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3
        }}
        style={{
          position: 'absolute',
          top: '25%',
          right: '12%',
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(187, 255, 193, 0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 1,
        }}
      />

      {/* Main Content Container */}
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 1, 
          ease: [0.4, 0, 0.2, 1],
          delay: 0.3
        }}
        style={{ zIndex: 2, width: '100%', maxWidth: 620 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6, md: 8 },
            width: '100%',
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            borderRadius: { xs: 3, sm: 4, md: 5 },
            textAlign: 'center',
            boxShadow: `
              0 25px 80px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            `,
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #f92955, #BBFFC1, #f92955)',
              zIndex: 1,
            }
          }}
        >
          {/* Logo Section with Enhanced Styling */}
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="Cariya Wallet Logo"
              sx={{
                width: { xs: 90, sm: 110, md: 120 },
                height: { xs: 90, sm: 110, md: 120 },
                mb: 4,
                borderRadius: 4,
                boxShadow: `
                  0 12px 32px rgba(249, 41, 85, 0.25),
                  0 4px 16px rgba(187, 255, 193, 0.15),
                  inset 0 1px 0 rgba(255, 255, 255, 0.3)
                `,
                border: '3px solid rgba(249, 41, 85, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `
                    0 16px 40px rgba(249, 41, 85, 0.35),
                    0 6px 20px rgba(187, 255, 193, 0.2),
                    inset 0 1px 0 rgba(255, 255, 255, 0.4)
                  `,
                }
              }}
            />
          </motion.div>

          {/* Enhanced Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                mb: 2,
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3rem' },
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              Cariya Wallet
            </Typography>
            
            {/* Enhanced Subtitle with Better Typography */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h5"
                sx={{
                  color: '#2c3e50',
                  mb: 1.5,
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                  lineHeight: 1.4,
                }}
              >
                Co-invest with mothers and grow their living wages
              </Typography>
              
            </Box>
          </motion.div>

          {/* Enhanced Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -15, scale: 0.95 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <Alert 
                  severity="error" 
                  icon={<Error sx={{ color: '#d32f2f' }} />}
                  sx={{ 
                    mb: 5, 
                    borderRadius: 3,
                    border: '1px solid rgba(211, 47, 47, 0.2)',
                    boxShadow: '0 4px 16px rgba(211, 47, 47, 0.1)',
                    '& .MuiAlert-message': {
                      fontWeight: 600,
                      color: '#d32f2f',
                    },
                    '& .MuiAlert-icon': {
                      alignItems: 'center',
                    }
                  }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Enhanced Login Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: "easeOut" }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Enhanced Email Field */}
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: activeField === 'email' ? 'rgba(249, 41, 85, 0.1)' : 'rgba(187, 255, 193, 0.1)',
                          transition: 'all 0.3s ease',
                        }}>
                          <Email 
                            sx={{ 
                              color: activeField === 'email' ? '#f92955' : '#BBFFC1',
                              fontSize: 24,
                              transition: 'all 0.3s ease'
                            }} 
                          />
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: formData.email && (
                      <InputAdornment position="end">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: formValid.email ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                        }}>
                          {formValid.email ? (
                            <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                          ) : (
                            <Error sx={{ color: '#F44336', fontSize: 20 }} />
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 3,
                      bgcolor: '#FAFAFA',
                      transition: 'all 0.3s ease',
                      height: 64,
                      '&:hover': {
                        bgcolor: '#F5F5F5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 4px rgba(249, 41, 85, 0.1)',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { 
                        borderColor: activeField === 'email' ? '#f92955' : '#E0E0E0',
                        borderWidth: activeField === 'email' ? 2 : 1,
                        transition: 'all 0.3s ease',
                      },
                      '&:hover fieldset': { 
                        borderColor: activeField === 'email' ? '#d41e3f' : '#BDBDBD',
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: '#f92955',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#666666',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                    },
                    '& .MuiInputLabel-root.Mui-focused': { 
                      color: '#f92955',
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>

              {/* Enhanced Phone Number Field */}
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  onFocus={() => handleFocus('phoneNumber')}
                  onBlur={handleBlur}
                  fullWidth
                  variant="outlined"
                  placeholder="+1234567890"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: activeField === 'phoneNumber' ? 'rgba(249, 41, 85, 0.1)' : 'rgba(187, 255, 193, 0.1)',
                          transition: 'all 0.3s ease',
                        }}>
                          <Phone 
                            sx={{ 
                              color: activeField === 'phoneNumber' ? '#f92955' : '#BBFFC1',
                              fontSize: 24,
                              transition: 'all 0.3s ease'
                            }} 
                          />
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: formData.phoneNumber && (
                      <InputAdornment position="end">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: formValid.phoneNumber ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                        }}>
                          {formValid.phoneNumber ? (
                            <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                          ) : (
                            <Error sx={{ color: '#F44336', fontSize: 20 }} />
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 3,
                      bgcolor: '#FAFAFA',
                      transition: 'all 0.3s ease',
                      height: 64,
                      '&:hover': {
                        bgcolor: '#F5F5F5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 4px rgba(249, 41, 85, 0.1)',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { 
                        borderColor: activeField === 'phoneNumber' ? '#f92955' : '#E0E0E0',
                        borderWidth: activeField === 'phoneNumber' ? 2 : 1,
                        transition: 'all 0.3s ease',
                      },
                      '&:hover fieldset': { 
                        borderColor: activeField === 'phoneNumber' ? '#d41e3f' : '#BDBDBD',
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: '#f92955',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#666666',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                    },
                    '& .MuiInputLabel-root.Mui-focused': { 
                      color: '#f92955',
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>

              {/* Enhanced Login Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ marginTop: 8 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid || isLoading}
                  sx={{
                    background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
                    color: '#FFFFFF',
                    borderRadius: 3,
                    py: 3,
                    px: 5,
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                    fontWeight: 700,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    boxShadow: `
                      0 12px 32px rgba(249, 41, 85, 0.4),
                      0 4px 16px rgba(187, 255, 193, 0.2)
                    `,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: 68,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d41e3f 0%, #b01a35 100%)',
                      boxShadow: `
                        0 16px 40px rgba(249, 41, 85, 0.6),
                        0 6px 20px rgba(187, 255, 193, 0.3)
                      `,
                      transform: 'translateY(-3px)',
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
                      color: '#999999',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                  fullWidth
                >
                  {isLoading ? (
                    <CircularProgress size={26} sx={{ color: '#FFFFFF' }} />
                  ) : (
                    <>
                      <Lock sx={{ mr: 1.5, fontSize: 22 }} />
                      Sign In
                      <ArrowForward sx={{ ml: 1.5, fontSize: 22 }} />
                    </>
                  )}
                </Button>
              </motion.div>
            </Box>
          </motion.form>

          {/* Enhanced Demo Login Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: "easeOut" }}
          >
            <Divider sx={{ 
              my: 5, 
              '&::before, &::after': { 
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderWidth: 1,
              } 
            }}>
              <Chip 
                label="Quick Demo Access" 
                sx={{ 
                  bgcolor: 'rgba(249, 41, 85, 0.08)',
                  color: '#f92955',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2,
                  py: 1,
                  border: '1px solid rgba(249, 41, 85, 0.2)',
                }} 
              />
            </Divider>

            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button
                variant="outlined"
                onClick={() => handleDemoLogin('admin')}
                disabled={isLoading}
                startIcon={<AccountCircle sx={{ fontSize: 20 }} />}
                sx={{
                  borderColor: '#f92955',
                  color: '#f92955',
                  borderRadius: 3,
                  fontWeight: 600,
                  py: 1.5,
                  px: 3,
                  fontSize: '1rem',
                  borderWidth: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#d41e3f',
                    bgcolor: 'rgba(249, 41, 85, 0.08)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(249, 41, 85, 0.2)',
                  },
                }}
              >
                Admin Demo
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleDemoLogin('partner')}
                disabled={isLoading}
                startIcon={<VerifiedUser sx={{ fontSize: 20 }} />}
                sx={{
                  borderColor: '#BBFFC1',
                  color: '#2c3e50',
                  borderRadius: 3,
                  fontWeight: 600,
                  py: 1.5,
                  px: 3,
                  fontSize: '1rem',
                  borderWidth: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#a8ffae',
                    bgcolor: 'rgba(187, 255, 193, 0.15)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(187, 255, 193, 0.3)',
                  },
                }}
              >
                Partner Demo
              </Button>
            </Box>
          </motion.div>

          {/* Enhanced Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.6, ease: "easeOut" }}
          >
            <Box sx={{ 
              mt: 6, 
              p: 3, 
              borderRadius: 3,
              bgcolor: 'rgba(249, 41, 85, 0.05)',
              border: '1px solid rgba(249, 41, 85, 0.1)',
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#7f8c8d',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                }}
              >
                🔒 Secure login powered by Cariya Wallet
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#95a5a6',
                  fontSize: '0.8rem',
                  fontWeight: 400,
                  display: 'block',
                  mt: 0.5,
                }}
              >
                Enterprise-grade security & encryption
              </Typography>
            </Box>
          </motion.div>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default Login;